<div class="tab-content role-management">
  <!-- RBAC Teaser Banner for non-RBAC accounts -->
  @if (!rbacFullEnabled) {
    <div class="rbac-teaser-banner">
      <i class="fa-utility fa-semibold fa-lock"></i>
      <span>
        <strong>Unlock Role-Based Access Control</strong> — Create custom roles with granular permissions. Contact your
        account manager to enable this feature.
      </span>
    </div>
  }
  @if (rbacFullEnabled) {
    <div>
      Control user access in Arpio through roles. Account roles define what actions users can take. Custom roles go
      further by also limiting which applications those actions apply to.
      <a [href]="uiService.rbacDocsUrl" target="_blank">Learn more about roles.</a>
    </div>
  }
  <div class="flex justify-between margin-top">
    <div class="flex items-center">
      <h2>All Roles</h2>
      @if (canManageRoles$ | async) {
        <a class="underline margin-left" (click)="openNewRoleModal()" data-cy="create-role-button">
          <i class="fa-utility fa-semibold fa-plus text-sm"></i>Create Custom Role
        </a>
      }
    </div>
    <div class="half-margin-bottom">
      <div class="filter-container">
        <input pInputText #filterInput type="text" (input)="filterGlobal($event)" placeholder="Search..." />
        @if (!isFilterApplied && !loadingFilterInput) {
          <div class="fa-utility-fill fa-semibold fa-filter fade-icon"></div>
        }
        @if (isFilterApplied && !loadingFilterInput) {
          <div (click)="clearInput()" class="fa-utility fa-semibold fa-xmark"></div>
        }
        @if (loadingFilterInput) {
          <div class="fa-solid fa-spinner fa-spin"></div>
        }
      </div>
    </div>
  </div>
  <!-- Unauthorized State -->
  @if ((canViewRoles$ | async) === false) {
    <div class="unauthorized-container" data-cy="unauthorized-message">
      <div class="unauthorized-content">
        <h3>Access Restricted</h3>
        <p>{{ unauthorizedMessage$ | async }}</p>
      </div>
    </div>
  }

  <!-- Loading State - show while data hasn't loaded yet -->
  @if (roles === null) {
    <div class="loading-container"><i class="fa-solid fa-spinner fa-spin"></i> Loading roles...</div>
  }

  <!-- Authorized Content - only show when roles array exists (even if empty) -->
  @if (roles !== null && (canViewRoles$ | async) !== false) {
    <div class="table-container">
      <!-- Empty State -->
      @if (roles.length === 0) {
        <div class="empty-state-container">
          <div class="empty-state-content">
            <h3>No roles have been created yet</h3>
            <p>
              Roles define what users can see or do in Arpio. Create your first role to get started managing access
              permissions.
            </p>
            @if (canManageRoles$ | async) {
              <button class="p-button p-button-primary" (click)="openNewRoleModal()">
                <i class="fa-utility fa-semibold fa-plus"></i>
                Create Your First Role
              </button>
            }
          </div>
        </div>
      }
      <!-- Table Section - Only show when roles exist -->
      @if (roles.length > 0) {
        <div class="table-section main-table">
          <p-table
            #dt1
            [value]="roles"
            [globalFilterFields]="globalFilterFields"
            data-cy="role-table"
            dataKey="id"
            [scrollable]="true"
            scrollHeight="60vh"
            [tableStyle]="{ 'min-width': '30rem' }"
            [styleClass]="'p-datatable-sm arp-prime-datatable'"
            [rowHover]="true"
            [totalRecords]="roles?.length"
            [paginator]="false"
            [rowTrackBy]="roleTrackBy"
            selectionMode="single"
            [(selection)]="selectedTableItem"
            (onRowSelect)="onRowSelect($event.data)"
            [customSort]="true"
            (sortFunction)="customSort($event)"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="name" class="strong-text name-column">
                  Name <p-sortIcon field="name"></p-sortIcon>
                </th>
                <!-- <th pSortableColumn="accessLevel" class="strong-text access-level-column">
                Access Level(s) <p-sortIcon field="accessLevel"></p-sortIcon>
              </th>
              <th pSortableColumn="applications" class="strong-text applications-column">
                Applications <p-sortIcon field="applications"></p-sortIcon>
              </th> -->
                <th pSortableColumn="externalId" class="strong-text external-id-column">
                  External ID <p-sortIcon field="externalId"></p-sortIcon>
                </th>
                <th pSortableColumn="policyCount" class="strong-text policy-count-column">
                  Policies <p-sortIcon field="policyCount"></p-sortIcon>
                </th>
                <th pSortableColumn="modifiedAt" class="strong-text last-modified-column">
                  Last Modified <p-sortIcon field="modifiedAt"></p-sortIcon>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-role let-index="rowIndex">
              <tr
                [pSelectableRow]="role"
                class="cursor-pointer"
                [ngClass]="{
                  'selected-row': showSidePanel && selectedRole && selectedRole.id === role.id,
                  'default-role-row': isDefaultAccountRole(role),
                  'refreshing-row': isRoleRefreshing(role),
                }"
              >
                <td class="name-column" style="position: relative">
                  <div class="role-info">
                    <div class="rbac-table-display-container">
                      @if (isRoleRefreshing(role)) {
                        <i class="fa-solid fa-spinner fa-spin half-margin-right"></i>
                      }
                      <span>{{ role.name }}</span>
                    </div>
                  </div>
                </td>
                <!-- <td class="access-level-column">
              <i class="fa-solid fa-spinner fa-spin half-margin-right" *ngIf="isRoleRefreshing(role)"></i>
              <div class="access-level-badges">
                <p-tag
                  *ngFor="let accessLevelInfo of role.accessLevels"
                  [severity]="
                    getAccessLevelClass(accessLevelInfo.accessLevel) === 'account-admin'
                      ? 'danger'
                      : getAccessLevelClass(accessLevelInfo.accessLevel) === 'account-read'
                        ? 'warning'
                        : 'info'
                  "
                  class="access-level-badge"
                  [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                  tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                  tooltipPosition="top"
                  [autoHide]="false"
                  [hideDelay]="200"
                  [escape]="false"
                  tooltipEvent="hover"
                  value="{{ getAccessLevelDisplay(accessLevelInfo.accessLevel) }}"
                  >
                </p-tag>
              </div>
            </td>
            <td class="applications-column">
              <div class="flex items-center">
                <i class="fa-solid fa-spinner fa-spin half-margin-right" *ngIf="isRoleRefreshing(role)"></i>
                <div class="applications-list">
                  <p-tag
                    *ngFor="let app of role.applications | slice: 0 : 3"
                    severity="secondary"
                    [class]="app === 'All Applications' ? 'app-tag fit-width' : 'app-tag clickable-tag fit-width'"
                    (click)="app !== 'All Applications' && navigateToApplication(app)"
                    value="{{ app }}"
                    >
                  </p-tag>
                  <span
                    *ngIf="role.applications.length > 3"
                    class="more-count"
                    [pTooltip]="getAdditionalApplicationsTooltip(role)"
                    tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                    tooltipPosition="top"
                    [autoHide]="false"
                    [hideDelay]="200"
                    [escape]="false"
                    tooltipEvent="hover"
                    >
                    +{{ (role.applicationCount || role.applications.length) - 3 }} more
                  </span>
                </div>
              </div>
            </td> -->
                <td class="external-id-column">
                  @if (role.externalId) {
                    <span class="external-id">{{ role.externalId }}</span>
                  } @else {
                    <span class="text-muted"><em>None</em></span>
                  }
                </td>
                <td class="policy-count-column">
                  <span class="policy-count">{{ role.policyCount }}</span>
                </td>
                <td class="last-modified-column">
                  <div class="rbac-table-display-container">
                    <span
                      [pTooltip]="getLastModifiedTooltip(role.modifiedAt)"
                      tooltipStyleClass="arp-tooltip"
                      tooltipPosition="top"
                      [autoHide]="false"
                      [hideDelay]="200"
                      tooltipEvent="hover"
                      >{{ getLastModifiedDisplay(role.modifiedAt) }}</span
                    >
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="empty-state-cell text-center">
                  <div class="empty-state-content">
                    <p class="empty-state-message">No results found</p>
                    <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="summary">
              <div class="flex align-items-center justify-content-between">
                <span
                  >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of
                  {{ dt1.value?.length }} roles</span
                >
              </div>
            </ng-template>
          </p-table>
        </div>
      }
      <!-- Side Panel -->
      <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel>
        <div class="side-panel-header">
          <div class="flex items-center">
            <button class="close-btn" (click)="closeSidePanel()">
              <i class="fa-utility fa-semibold fa-angle-right"></i>
            </button>
            <h3>Role Details</h3>
          </div>
          <div>
            @if ((canManageRoles$ | async) && canEditRole(selectedRole)) {
              <button class="edit-button" (click)="openEditRoleModal()">
                <i class="fa-utility fa-semibold fa-pen-to-square"></i>
              </button>
            }
          </div>
        </div>
        <div class="side-panel-content">
          <!-- Basic Information Section -->
          <div class="detail-section">
            <div class="detail-item">
              <label>Name:</label>
              @if (showSidePanel && selectedRole) {
                <span>{{ selectedRole.name }}</span>
              }
              @if (!showSidePanel || !selectedRole) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <label>External ID:</label>
              @if (showSidePanel && selectedRole?.externalId) {
                <span>{{ selectedRole.externalId }}</span>
              } @else {
                @if (showSidePanel && selectedRole) {
                  <span class="text-muted"><em>None</em></span>
                }
                @if (!showSidePanel || !selectedRole) {
                  <div class="detail-skeleton"></div>
                }
              }
            </div>
            @if (showSidePanel && selectedRole && selectedRole.isSystemRole) {
              <div class="detail-item">
                <label></label>
                <em class="text-xs">System role policies cannot be modified.</em>
              </div>
            }
          </div>
          <!-- Policies Section -->
          <div class="detail-section">
            <h4>Policies ({{ getRolePolicies().length }})</h4>
            <div class="detail-item">
              @if (showSidePanel && selectedRoleDetails && getRolePolicies().length > 0) {
                <div class="policies-list">
                  @for (policy of getRolePolicies(); track policy; let i = $index) {
                    <div class="policy-item">
                      <div class="policy-header half-half-margin-bottom">
                        <h5>Policy {{ i + 1 }}</h5>
                      </div>
                      <div class="policy-description text-sm text-muted margin-bottom">
                        @if (getPolicyDescriptionComponents(policy); as components) {
                          <p-tag
                            severity="info"
                            class="access-level-tag-display fit-width"
                            style="display: inline-flex; margin-right: 0.25rem"
                            value="{{ components.accessLevel }}"
                          >
                          </p-tag
                          >{{ components.prefix }}
                          @if (components.showTooltip) {
                            <span
                              [pTooltip]="components.tooltipText"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                              class="text-underline-dotted"
                              >{{ components.applicationsText }}</span
                            >
                          }
                          @if (!components.showTooltip) {
                            <span>{{ components.applicationsText }}</span>
                          }
                          {{ components.suffix }}
                        }
                      </div>
                    </div>
                  }
                </div>
              }
              @if (showSidePanel && selectedRoleDetails && getRolePolicies().length === 0) {
                <div class="no-data-text">No policies defined</div>
              }
              @if (showSidePanel && !selectedRoleDetails) {
                <div class="no-data-text">Loading policies...</div>
              }
              @if (!showSidePanel) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Applications Section -->
          <!-- <div class="detail-section">
              <h4 *ngIf="showSidePanel && selectedRole">
                Applications ({{ selectedRole.applicationCount || selectedRole.applications.length }})
              </h4>
              <h4 *ngIf="!showSidePanel || !selectedRole">Applications</h4>
              <div class="applications-list">
                <ng-container *ngIf="showSidePanel && selectedRole">
                  <p-tag
                    *ngFor="let app of selectedRole.applications"
                    [value]="app"
                    severity="secondary"
                    [class]="app === 'All Applications' ? 'app-tag' : 'app-tag clickable-tag'"
                    (click)="app !== 'All Applications' && navigateToApplication(app)"
                  ></p-tag>
                </ng-container>
                <ng-container *ngIf="!showSidePanel || !selectedRole">
                  <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                  <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                  <div class="tag-skeleton"></div>
                </ng-container>
              </div>
            </div> -->
          <!-- Additional Information Section -->
          @if (
            !showSidePanel ||
            !selectedRole ||
            !isDefaultAccountRole(selectedRole) ||
            (canDeleteRole(selectedRole) && (canManageRoles$ | async))
          ) {
            <div class="detail-section">
              <h4>Additional Information</h4>
              @if (!showSidePanel || !selectedRole || !isDefaultAccountRole(selectedRole)) {
                <div class="detail-item">
                  <label>Created:</label>
                  @if (showSidePanel && selectedRole) {
                    <span>{{ selectedRole.createdAt | date: 'medium' }}</span>
                  }
                  @if (!showSidePanel || !selectedRole) {
                    <div class="detail-skeleton"></div>
                  }
                </div>
              }
              @if (!showSidePanel || !selectedRole || !isDefaultAccountRole(selectedRole)) {
                <div class="detail-item">
                  <label>Last Modified:</label>
                  @if (showSidePanel && selectedRole) {
                    <span>{{ selectedRole.modifiedAt | date: 'medium' }}</span>
                  }
                  @if (!showSidePanel || !selectedRole) {
                    <div class="detail-skeleton"></div>
                  }
                </div>
              }
            </div>
          }
          <!-- Action Buttons Section -->
          @if (showSidePanel && selectedRole && canDeleteRole(selectedRole) && (canManageRoles$ | async)) {
            <div class="detail-section action-buttons">
              <h4>Actions</h4>
              <div class="margin-auto text-center margin-top">
                <a class="delete-role-button text-center" (click)="deleteRole()">Delete Role...</a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <ng-template #loadingBlock> <i class="fa-solid fa-spinner fa-spin"></i> Loading roles... </ng-template>

  <app-new-role-modal #newRoleModal (roleCreated)="onRoleCreated()"></app-new-role-modal>
  <app-edit-role-modal #editRoleModal (roleUpdated)="onRoleUpdated($event)"></app-edit-role-modal>

  <!-- Delete Role Confirmation Dialog -->
  <modal-dialog #deleteRoleDialog [dialogTemplate]="confirmDeleteRoleDialog"></modal-dialog>
  <ng-template #confirmDeleteRoleDialog>
    <confirm-action-dialog
      name="&quot;{{ roleToDelete?.name }}&quot;"
      action="Delete"
      [error]="deleteRoleDialog.error"
      (onCancel)="cancelDeleteRole()"
      (onAction)="confirmDeleteRole()"
    >
      <div class="error margin-top">
        Deleting this role will remove it from all assigned users and groups. This action cannot be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
